import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtUGX } from "@/lib/format";
import { toast } from "sonner";
import { Puzzle, Package, Search, Sparkles, ShieldCheck, Lock, Zap, Plus } from "lucide-react";

type ScopeType = "school" | "student" | "user" | "merchant";

type Feature = {
  feature_key: string; name: string; category: string;
  enabled: boolean; source: "override" | "plan" | "default";
  is_addon: boolean; price_ugx_monthly: number;
};

export function FeatureModulesConsole() {
  const qc = useQueryClient();
  const [scopeType, setScopeType] = useState<ScopeType>("school");
  const [scopeId, setScopeId] = useState<string>("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"entitlements" | "plans" | "catalog">("entitlements");

  // Scope pickers
  const schools = useQuery({
    queryKey: ["fm-schools"],
    queryFn: async () => (await supabase.from("schools").select("id,name").order("name").limit(200)).data ?? [],
  });
  const students = useQuery({
    enabled: scopeType === "student",
    queryKey: ["fm-students"],
    queryFn: async () => (await supabase.from("students").select("id,full_name,student_no").order("full_name").limit(300)).data ?? [],
  });
  const merchants = useQuery({
    enabled: scopeType === "merchant",
    queryKey: ["fm-merchants"],
    queryFn: async () => (await supabase.from("merchants").select("id,name").order("name").limit(300)).data ?? [],
  });

  const scopeOptions = useMemo(() => {
    if (scopeType === "school")  return (schools.data ?? []).map((s: any) => ({ id: s.id, label: s.name }));
    if (scopeType === "student") return (students.data ?? []).map((s: any) => ({ id: s.id, label: `${s.full_name} · ${s.student_no ?? ""}` }));
    if (scopeType === "merchant")return (merchants.data ?? []).map((m: any) => ({ id: m.id, label: m.name }));
    return [];
  }, [scopeType, schools.data, students.data, merchants.data]);

  // Effective features
  const effective = useQuery({
    enabled: !!scopeId,
    queryKey: ["fm-effective", scopeType, scopeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_effective_features", { _scope_type: scopeType, _scope_id: scopeId });
      if (error) throw error;
      return (data ?? []) as Feature[];
    },
  });

  const plans = useQuery({
    queryKey: ["fm-plans"],
    queryFn: async () => (await supabase.from("subscription_plans").select("*").order("price_ugx_monthly")).data ?? [],
  });
  const sub = useQuery({
    enabled: !!scopeId,
    queryKey: ["fm-sub", scopeType, scopeId],
    queryFn: async () => (await supabase.from("tenant_subscriptions").select("*, subscription_plans(*)")
      .eq("scope_type", scopeType).eq("scope_id", scopeId).maybeSingle()).data,
  });
  const catalog = useQuery({
    queryKey: ["fm-catalog"],
    queryFn: async () => (await supabase.from("feature_catalog").select("*").order("category")).data ?? [],
  });

  const toggle = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const { error } = await supabase.rpc("set_feature_entitlement", {
        _scope_type: scopeType, _scope_id: scopeId, _feature_key: key, _enabled: enabled,
      });
      if (error) throw error;
    },
    onSuccess: (_, v) => {
      toast.success(v.enabled ? "Feature enabled" : "Feature disabled");
      qc.invalidateQueries({ queryKey: ["fm-effective", scopeType, scopeId] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const assignPlan = useMutation({
    mutationFn: async (planId: string) => {
      const { error } = await supabase.rpc("assign_subscription_plan", { _scope_type: scopeType, _scope_id: scopeId, _plan_id: planId });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Plan assigned");
      qc.invalidateQueries({ queryKey: ["fm-effective", scopeType, scopeId] });
      qc.invalidateQueries({ queryKey: ["fm-sub", scopeType, scopeId] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    const list = effective.data ?? [];
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter(f => f.name.toLowerCase().includes(s) || f.category.toLowerCase().includes(s) || f.feature_key.includes(s));
  }, [effective.data, q]);

  const byCategory = useMemo(() => {
    const g: Record<string, Feature[]> = {};
    for (const f of filtered) (g[f.category] ??= []).push(f);
    return g;
  }, [filtered]);

  const totalMonthly = useMemo(() =>
    (effective.data ?? []).filter(f => f.enabled && f.is_addon).reduce((a, f) => a + f.price_ugx_monthly, 0),
    [effective.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Puzzle className="w-6 h-6" />
            <h2 className="text-2xl font-semibold">Modules & entitlements</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Toggle features per school, student, merchant, or user. Overrides always win over the assigned plan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline"><ShieldCheck className="w-3 h-3 mr-1" /> Superadmin-gated</Badge>
        </div>
      </div>

      {/* Scope picker */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-3 items-end">
          <div>
            <Label>Scope</Label>
            <Select value={scopeType} onValueChange={(v: any) => { setScopeType(v); setScopeId(""); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="merchant">Merchant</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target</Label>
            {scopeType === "user" ? (
              <Input placeholder="Paste user UUID" value={scopeId} onChange={e => setScopeId(e.target.value)} />
            ) : (
              <Select value={scopeId} onValueChange={setScopeId}>
                <SelectTrigger><SelectValue placeholder={`Pick a ${scopeType}…`} /></SelectTrigger>
                <SelectContent>
                  {scopeOptions.map(o => (<SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col items-end">
            <div className="text-xs text-muted-foreground">Add-ons monthly</div>
            <div className="text-lg font-semibold">{fmtUGX(totalMonthly)}</div>
          </div>
        </div>

        {scopeId && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
            <span className="text-sm text-muted-foreground">Subscription plan:</span>
            <Select value={sub.data?.plan_id ?? ""} onValueChange={(v) => assignPlan.mutate(v)}>
              <SelectTrigger className="w-64"><SelectValue placeholder="Choose plan" /></SelectTrigger>
              <SelectContent>
                {(plans.data ?? []).map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · {fmtUGX(p.price_ugx_monthly)}/mo</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sub.data?.subscription_plans && (
              <Badge variant="secondary" className="ml-2">
                <Package className="w-3 h-3 mr-1" />
                {(sub.data.subscription_plans as any).name} · {(sub.data.subscription_plans as any).tier}
              </Badge>
            )}
          </div>
        )}
      </Card>

      <Tabs value={tab} onValueChange={(v: any) => setTab(v)}>
        <TabsList>
          <TabsTrigger value="entitlements"><Zap className="w-4 h-4 mr-1" />Entitlements</TabsTrigger>
          <TabsTrigger value="plans"><Package className="w-4 h-4 mr-1" />Plans</TabsTrigger>
          <TabsTrigger value="catalog"><Sparkles className="w-4 h-4 mr-1" />Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="entitlements" className="space-y-4">
          {!scopeId ? (
            <Card className="p-10 text-center text-muted-foreground">Pick a scope above to manage features.</Card>
          ) : (
            <>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search features…" value={q} onChange={e => setQ(e.target.value)} />
              </div>
              {Object.entries(byCategory).map(([cat, feats]) => (
                <Card key={cat} className="p-0 overflow-hidden">
                  <div className="px-4 py-2 bg-muted/50 border-b flex items-center justify-between">
                    <div className="font-medium capitalize">{cat}</div>
                    <div className="text-xs text-muted-foreground">
                      {feats.filter(f => f.enabled).length} / {feats.length} enabled
                    </div>
                  </div>
                  <div className="divide-y">
                    {feats.map(f => (
                      <div key={f.feature_key} className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium truncate">{f.name}</div>
                            {f.is_addon && <Badge variant="outline" className="text-[10px]">Add-on</Badge>}
                            <Badge variant={f.source === "override" ? "default" : f.source === "plan" ? "secondary" : "outline"} className="text-[10px]">
                              {f.source}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground font-mono truncate">{f.feature_key}</div>
                        </div>
                        <div className="text-sm text-muted-foreground w-24 text-right">
                          {f.price_ugx_monthly ? `${fmtUGX(f.price_ugx_monthly)}/mo` : "Included"}
                        </div>
                        <Switch
                          checked={f.enabled}
                          disabled={toggle.isPending}
                          onCheckedChange={(v) => toggle.mutate({ key: f.feature_key, enabled: v })}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="plans" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(plans.data ?? []).map((p: any) => (
              <Card key={p.id} className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold">{p.name}</div>
                    <div className="text-xs uppercase text-muted-foreground">{p.tier}</div>
                  </div>
                  <Package className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="mt-3 text-2xl font-bold">{fmtUGX(p.price_ugx_monthly)}<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                <PlanFeatureList planId={p.id} />
                {scopeId && (
                  <Button className="w-full mt-4" size="sm" variant="outline" onClick={() => assignPlan.mutate(p.id)}>
                    Assign to current scope
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="catalog" className="space-y-2">
          <Card className="p-0 overflow-hidden">
            <div className="grid grid-cols-[1fr_100px_100px_100px] px-4 py-2 text-xs uppercase text-muted-foreground bg-muted/50 border-b">
              <div>Feature</div><div>Category</div><div>Price/mo</div><div>Default</div>
            </div>
            <div className="divide-y">
              {(catalog.data ?? []).map((f: any) => (
                <div key={f.key} className="grid grid-cols-[1fr_100px_100px_100px] px-4 py-2 items-center">
                  <div>
                    <div className="font-medium">{f.name} {f.is_addon && <Badge variant="outline" className="text-[10px] ml-1">Add-on</Badge>}</div>
                    <div className="text-xs text-muted-foreground font-mono">{f.key}</div>
                  </div>
                  <div className="text-sm capitalize">{f.category}</div>
                  <div className="text-sm">{f.price_ugx_monthly ? fmtUGX(f.price_ugx_monthly) : "—"}</div>
                  <div>{f.default_enabled ? <Badge>On</Badge> : <Badge variant="outline">Off</Badge>}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlanFeatureList({ planId }: { planId: string }) {
  const { data } = useQuery({
    queryKey: ["fm-plan-feats", planId],
    queryFn: async () => (await supabase.from("plan_features").select("feature_key, feature_catalog(name)").eq("plan_id", planId)).data ?? [],
  });
  return (
    <ul className="mt-3 space-y-1 text-sm max-h-40 overflow-auto">
      {(data ?? []).map((r: any) => (
        <li key={r.feature_key} className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {(r.feature_catalog as any)?.name ?? r.feature_key}
        </li>
      ))}
    </ul>
  );
}
